import { useEffect, useState } from "react";
import { Heart, Handshake, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, inputCls } from "@/components/kit";
import { api, errMsg, fmtDate, initials, onColor } from "@/lib/api";
import { FormDialog } from "@/pages/manage/common";

export const useCountdown = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(iso).getTime() - now);
  return { d: Math.floor(diff / 864e5), h: Math.floor(diff / 36e5) % 24, m: Math.floor(diff / 6e4) % 60, s: Math.floor(diff / 1e3) % 60 };
};

export const Countdown = ({ iso }) => {
  const c = useCountdown(iso);
  return (
    <div className="flex gap-3" data-testid="next-match-countdown">
      {[["d", "Days"], ["h", "Hrs"], ["m", "Min"], ["s", "Sec"]].map(([k, l]) => (
        <div key={k} className="min-w-[60px] rounded-md border border-white/10 bg-black/40 px-3 py-2 text-center backdrop-blur">
          <p className="font-display text-3xl font-black tabular-nums text-white">{String(c[k]).padStart(2, "0")}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{l}</p>
        </div>
      ))}
    </div>
  );
};

export const ResultBadge = ({ e }) => {
  const r = e.our_score > e.their_score ? ["W", "bg-emerald-500"] : e.our_score === e.their_score ? ["D", "bg-amber-500"] : ["L", "bg-rose-500"];
  return <span className={`flex h-7 w-7 items-center justify-center rounded font-display text-sm font-black text-black ${r[1]}`}>{r[0]}</span>;
};

export const HubPost = ({ p, slug, color }) => {
  const [likes, setLikes] = useState(p.likes);
  const [poll, setPoll] = useState(p.poll);
  const liked = localStorage.getItem(`pp-like-${p.id}`);
  const voted = localStorage.getItem(`pp-vote-${p.id}`);
  const like = async () => {
    if (liked) return;
    const { data } = await api.post(`/public/teams/${slug}/posts/${p.id}/like`);
    localStorage.setItem(`pp-like-${p.id}`, "1");
    setLikes(data.likes);
  };
  const vote = async (i) => {
    const { data } = await api.post(`/public/teams/${slug}/posts/${p.id}/vote`, { option_index: i });
    localStorage.setItem(`pp-vote-${p.id}`, String(i));
    setPoll(data);
    toast.success("Vote counted!");
  };
  const total = poll ? poll.options.reduce((a, o) => a + o.votes, 0) || 1 : 1;
  return (
    <article className="overflow-hidden rounded-lg border border-white/[0.08] bg-[#111827]" data-testid={`hub-post-${p.id}`}>
      {p.image_url && <img src={p.image_url} alt={p.title} className="h-52 w-full object-cover" />}
      <div className="p-6">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{fmtDate(p.created_at)}</p>
        <h3 className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-white">{p.title}</h3>
        {p.body && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-300">{p.body}</p>}
        {poll && (
          <div className="mt-5 space-y-2" data-testid={`hub-poll-${p.id}`}>
            <p className="text-sm font-semibold text-white">{poll.question}</p>
            {poll.options.map((o, i) => (
              <button key={o.text} disabled={!!voted} onClick={() => vote(i)} data-testid={`poll-option-${p.id}-${i}`} className="relative block w-full overflow-hidden rounded border border-white/10 px-3 py-2.5 text-left text-sm transition-colors enabled:hover:border-white/40">
                {voted && <div className="absolute inset-y-0 left-0 opacity-25 transition-all duration-700" style={{ width: `${(o.votes / total) * 100}%`, background: color }} />}
                <span className="relative flex justify-between text-slate-100"><span>{o.text}</span>{voted && <span className="font-mono">{Math.round((o.votes / total) * 100)}%</span>}</span>
              </button>
            ))}
          </div>
        )}
        <button onClick={like} data-testid={`like-post-${p.id}`} className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-1.5 text-sm text-slate-300 transition-transform active:scale-95">
          <Heart className="h-4 w-4" style={liked ? { fill: color, color } : {}} /> <span data-testid={`like-count-${p.id}`}>{likes}</span>
        </button>
      </div>
    </article>
  );
};

const TIERS = [["gold", "Gold partners"], ["silver", "Silver partners"], ["bronze", "Bronze partners"], ["supporter", "Official supporters"]];

const EnquiryDialog = ({ open, onOpenChange, slug }) => {
  const [f, setF] = useState({ name: "", email: "", company: "", message: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      await api.post(`/public/teams/${slug}/sponsor-enquiry`, f);
      toast.success("Thanks! The club will be in touch.");
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="Become a sponsor" onSubmit={submit} submitLabel="Send enquiry" testid="enquiry-form">
      <Field label="Business name"><Input required value={f.company} onChange={set("company")} data-testid="enquiry-company-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Your name"><Input required value={f.name} onChange={set("name")} data-testid="enquiry-name-input" /></Field>
        <Field label="Email"><Input type="email" required value={f.email} onChange={set("email")} data-testid="enquiry-email-input" /></Field>
      </div>
      <Field label="Message"><textarea className={`${inputCls} h-24`} value={f.message} onChange={set("message")} data-testid="enquiry-message-input" /></Field>
    </FormDialog>
  );
};

export const SponsorWall = ({ sponsors, slug, color }) => {
  const [open, setOpen] = useState(false);
  return (
    <section id="sponsors" className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">Our sponsors</h2>
        <Button onClick={() => setOpen(true)} className="rounded-full font-bold" style={{ background: color, color: onColor(color) }} data-testid="become-sponsor-btn"><Handshake className="mr-1 h-4 w-4" /> Become a sponsor</Button>
      </div>
      {TIERS.map(([tier, label]) => {
        const list = sponsors.filter((s) => s.tier === tier);
        if (!list.length) return null;
        return (
          <div key={tier} className="mt-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--team-ink)" }}>{label}</p>
            <div className={`mt-4 grid gap-4 ${tier === "gold" ? "sm:grid-cols-2" : "sm:grid-cols-3 lg:grid-cols-4"}`}>
              {list.map((s) => (
                <a key={s.id} href={s.website || undefined} target="_blank" rel="noreferrer" data-testid={`hub-sponsor-${s.id}`} className="group flex items-center gap-4 rounded-lg border border-white/[0.08] bg-[#111827] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/25">
                  {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-14 w-14 rounded bg-white object-contain p-1" /> : <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-white/5 font-display text-xl font-black text-white">{initials(s.name)}</span>}
                  <div className="min-w-0"><p className="font-semibold text-white">{s.name}</p>{s.description && <p className="text-xs text-slate-400">{s.description}</p>}{s.website && <Globe className="mt-1 h-3 w-3 text-slate-500" />}</div>
                </a>
              ))}
            </div>
          </div>
        );
      })}
      {!sponsors.length && <p className="mt-6 text-slate-400">Be the first local business to back the team.</p>}
      <EnquiryDialog open={open} onOpenChange={setOpen} slug={slug} />
    </section>
  );
};

export const FollowForm = ({ slug, color, onFollowed }) => {
  const [f, setF] = useState({ name: "", email: "" });
  const [done, setDone] = useState(localStorage.getItem(`pp-follow-${slug}`));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/public/teams/${slug}/follow`, f);
      localStorage.setItem(`pp-follow-${slug}`, "1");
      setDone("1");
      toast.success(data.already ? "You're already following!" : "You're following the team!");
      onFollowed?.();
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (done) return <p className="font-display text-2xl font-bold uppercase" style={{ color: "var(--team-ink)" }} data-testid="follow-done-msg">You're on the team sheet. See you at the next match!</p>;
  return (
    <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" data-testid="follow-form">
      <Input placeholder="First name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="sm:max-w-[180px]" data-testid="follow-name-input" />
      <Input type="email" required placeholder="you@email.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="follow-email-input" />
      <Button type="submit" className="rounded-full px-6 font-bold uppercase" style={{ background: color, color: onColor(color) }} data-testid="follow-submit-btn">Follow</Button>
    </form>
  );
};
